// @ts-nocheck
// Live-calls grid on the shared DataTable (same component every list page
// uses). Columns are the LiveCall fields; the optional "Supervise" column
// reuses cti2's SnoopButtons + useSnoop so monitor / whisper / barge behave
// exactly as on the team-lead dashboard.
import React, { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import SnoopButtons from '@/features/cti2/team/SnoopButtons';
import type { LiveCall } from '../types';
import { toSnoopRow } from '../liveCallsModel';
import { AgentCell, CustomerCell, DirectionCell, DurationCell, RouteCell, StatusPill } from './LiveCallBits';

interface Props {
  tableId: string;
  calls: LiveCall[];
  now: number;
  isLoading?: boolean;
  onRowClick?: (call: LiveCall) => void;
  /** Pass a useSnoop() result to show the Supervise column. */
  snoop?: any;
  canSnoop?: boolean;
  pageSize?: number;
  leftContent?: React.ReactNode;
  toolbar?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  disableExport?: boolean;
}

export default function LiveCallsTable({
  tableId,
  calls,
  now,
  isLoading = false,
  onRowClick,
  snoop,
  canSnoop = false,
  pageSize = 25,
  leftContent,
  toolbar,
  emptyTitle = 'No live calls',
  emptyDescription = 'Calls appear here the moment they start and leave on hangup.',
  disableExport = true,
}: Props) {
  const columns = useMemo<ColumnDef<LiveCall>[]>(() => {
    const cols: ColumnDef<LiveCall>[] = [
      {
        id: 'direction',
        header: 'Direction',
        accessorFn: (r) => r.direction,
        cell: ({ row }) => <DirectionCell direction={row.original.direction} />,
        size: 120,
      },
      {
        id: 'customer',
        header: 'Customer',
        accessorFn: (r) => [r.customerName, r.customerNo].filter(Boolean).join(' '),
        cell: ({ row }) => <CustomerCell call={row.original} />,
      },
      {
        // IVR digits the customer pressed (live-calls `dtmf`, e.g. "1,2").
        id: 'keys',
        header: 'Keys pressed',
        accessorFn: (r) => r.dtmf || '',
        cell: ({ row }) =>
          row.original.dtmf ? (
            <span className="text-[12.5px] font-semibold tabular-nums">{row.original.dtmf}</span>
          ) : (
            <span className="text-[12px] text-muted-foreground">—</span>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (r) => r.status,
        cell: ({ row }) => <StatusPill call={row.original} now={now} />,
      },
      {
        id: 'agent',
        header: 'Agent',
        accessorFn: (r) => r.agent?.name || r.aiAgentId || '',
        cell: ({ row }) => <AgentCell call={row.original} />,
      },
      {
        id: 'route',
        header: 'Route',
        accessorFn: (r) => r.queueName || r.campaignId || '',
        cell: ({ row }) => <RouteCell call={row.original} />,
        enableSorting: false,
      },
      {
        id: 'duration',
        header: 'Duration',
        accessorFn: (r) => r.startedAt || 0,
        cell: ({ row }) => <DurationCell call={row.original} now={now} />,
        sortDescFirst: false,
      },
      {
        id: 'pbx',
        header: 'PBX',
        accessorFn: (r) => r.pbx,
        cell: ({ row }) => (
          <span className="flex flex-col">
            <span className="text-[12px] tabular-nums">{row.original.pbx}</span>
            <span className="truncate text-[10.5px] text-muted-foreground" title={row.original.traceId}>
              {String(row.original.traceId || '').slice(0, 8)}…
            </span>
          </span>
        ),
      },
    ];
    if (snoop) {
      cols.push({
        id: 'supervise',
        header: 'Supervise',
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <SnoopButtons row={toSnoopRow(row.original)} snoop={snoop} allowed={canSnoop} />
          </span>
        ),
      });
    }
    return cols;
  }, [now, snoop, canSnoop]);

  return (
    <DataTable
      tableId={tableId}
      columns={columns}
      data={calls}
      isLoading={isLoading}
      getRowId={(r) => r.id}
      onRowClick={onRowClick}
      pageSize={pageSize}
      leftContent={leftContent}
      toolbar={toolbar}
      disableSearch={Boolean(leftContent)}
      searchPlaceholder="Customer number, agent, queue…"
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      disableExport={disableExport}
      exportFilename="live-calls.csv"
      initialSorting={[{ id: 'duration', desc: true }]}
      initialColumnPinning={{ left: ['direction'] }}
    />
  );
}
